import { useTranslation } from 'react-i18next';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import { ActionIcon, Grid, Tooltip } from '@mantine/core';
import { useLocation } from 'react-router-dom';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import moment from 'moment';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMemo, useState } from 'react';
import { useGetSuppliesReport } from '../../../hooks/reports/queries';
import TableView from './TableView';
import { MRT_PaginationState } from 'mantine-react-table';
import { getRangeDate } from '../datePickerHelper';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { exportSupplyCostReport } from '../../../services/reports.service';
import { IconFileSpreadsheet } from '@tabler/icons-react';

export interface SuppliesReportTableInfoData {
  name: string;
  totalAmount: number;
  totalCost: number;
  usageUnit: string;
  purchaseUnit: string;
  supplyId: string;
  records: number;
}
const SuppliesReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons', 'Notifications']);
  const location = useLocation();
  const [dateRangePicker, setDateRange] = useState<[DateValue, DateValue]>([
    moment().startOf('month').toDate(),
    moment().endOf('month').toDate()
  ]);
  const { fromDate, toDate } = getRangeDate(dateRangePicker);
  const { downloadExcelV2, loadingExport } = useDownloadExcel();

  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 9
  });
  const {
    data,
    isLoading: isGetLoadingOrders,
    isError
  } = useGetSuppliesReport(searchParamStoreId, {
    fromDate,
    toDate
  });

  const tableInfoData: SuppliesReportTableInfoData[] = useMemo(() => {
    return data?.items || [];
  }, [data]);
  const totalRowsCount = useMemo(() => data?.items?.length || 0, [data]);
  const pages = useMemo(() => Math.ceil(totalRowsCount / 9) || -1, [totalRowsCount]);

  return (
    <CatalogContainer>
      <CatalogListHeader title={t('ReportsPage:suppliesReport.title')} showCreateButton={false} />
      <Grid>
        <Grid.Col span={{ xs: 12, md: 'auto' }}>
          <DatePickerInput
            clearable={false}
            type="range"
            label={t('FieldName:date')}
            placeholder={t('FieldName:date')}
            value={dateRangePicker}
            onChange={setDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport}
              onClick={() => {
                downloadExcelV2(async () => {
                  try {
                    return await exportSupplyCostReport({
                      store: searchParamStoreId || 'all',
                      fromDate,
                      toDate
                    });
                  } catch (e) {
                    throw e;
                  }
                }, `supply_cost_${searchParamStoreId || 'all'}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV">
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>
        <Grid.Col span={12}>
          <TableView
            tableInfoData={tableInfoData}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetLoadingOrders}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default SuppliesReport;
